import { Link } from 'react-router-dom';
import { Globe, Users, TrendingUp, Briefcase, ArrowRight, CheckCircle2, Building2, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const Services = () => {
  const services = [
    {
      icon: <Globe className="w-10 h-10 text-blue-700" />,
      title: 'Market Entry Strategy',
      description: 'Navigate new markets with confidence through our comprehensive market entry services.',
      features: [
        'Market research and opportunity assessment',
        'Regulatory and compliance guidance',
        'Entry strategy development and execution',
        'Risk assessment and mitigation planning',
        'Local market insights and competitive analysis'
      ],
      details: 'We help companies successfully enter Singapore, Asia, and global markets by providing deep market insights, identifying opportunities, and developing tailored entry strategies that align with your business objectives.'
    },
    {
      icon: <Users className="w-10 h-10 text-blue-700" />,
      title: 'Strategic Partnerships',
      description: 'Connect with the right partners to accelerate your growth and market presence.',
      features: [
        'Distributor and partner identification',
        'Industry expert introductions',
        'Ecosystem partner connections',
        'Partnership structuring and negotiation support',
        'Ongoing relationship management'
      ],
      details: 'Leverage our extensive network to connect with distributors, industry experts, and ecosystem partners who can help you scale your business and establish a strong market presence.'
    },
    {
      icon: <TrendingUp className="w-10 h-10 text-blue-700" />,
      title: 'Business Development Support',
      description: 'Drive commercial growth through strategic business development initiatives.',
      features: [
        'Go-to-market strategy development',
        'Sales channel optimization',
        'Commercial model design',
        'Customer acquisition strategy',
        'Growth acceleration programs'
      ],
      details: 'Our business development support helps you build and execute strategies that drive revenue growth, expand market share, and establish sustainable competitive advantages in new markets.'
    },
    {
      icon: <Briefcase className="w-10 h-10 text-blue-700" />,
      title: 'Capital Introduction',
      description: 'Access the right funding sources to fuel your expansion and growth plans.',
      features: [
        'Investor identification and targeting',
        'Family office introductions',
        'Institutional investor connections',
        'Fundraising strategy and positioning',
        'Investment readiness advisory'
      ],
      details: 'We connect growth-stage businesses with qualified investors, family offices, and institutional capital sources that align with your funding requirements and strategic objectives.'
    },
    {
      icon: <Building2 className="w-10 h-10 text-blue-700" />,
      title: 'Private Market Opportunities',
      description: 'Access to selected private market opportunities through qualified partners and investment structures.',
      features: [
        'Curated opportunity introduction',
        'Qualified partner connections',
        'Deal structure guidance',
        'Market intelligence and insights',
        'Strategic relationship facilitation'
      ],
      details: 'We facilitate access to selected private market opportunities through our network of qualified partners, providing strategic introductions and market insights for sophisticated investors.'
    }
  ];

  const faqs = [
    {
      question: 'How does Aquavera differ from traditional consulting firms?',
      answer: 'Aquavera combines strategic advisory with hands-on execution support and extensive network access. Unlike traditional consultants who deliver reports, we work alongside you to implement strategies, make introductions, and drive tangible results.'
    },
    {
      question: 'What industries do you specialize in?',
      answer: 'We focus on high-growth sectors including Healthcare, DeepTech, Artificial Intelligence, Energy, and Advanced Manufacturing. Our expertise in these industries enables us to provide specialized insights and targeted introductions.'
    },
    {
      question: 'How long does a typical market entry engagement take?',
      answer: 'Timeline varies based on market complexity and scope, but most market entry projects span 3-6 months from initial strategy to execution. We work at your pace and can accelerate timelines based on your business needs.'
    },
    {
      question: 'Do you work with early-stage companies?',
      answer: 'Yes, we work with companies at various stages, from growth-stage startups to established enterprises. Our services are tailored to your current stage and expansion objectives.'
    }
  ];

  return (
    <div className="flex-1">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl fade-in">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Comprehensive Services for Cross-Border Growth
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Strategic advisory services designed to accelerate your international expansion and drive sustainable growth.
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-24 bg-white px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-24">
          {services.map((service, index) => (
            <div key={index} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="mb-6">{service.icon}</div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">{service.title}</h2>
                <p className="text-lg text-slate-600 mb-6">{service.description}</p>
                <p className="text-slate-700 mb-8">{service.details}</p>
                <Link to="/contact">
                  <Button className="bg-blue-700 hover:bg-blue-800 text-white font-medium">
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
              <Card className={`border-slate-200 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold text-slate-900 mb-6">Key Capabilities</h3>
                  <ul className="space-y-4">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-blue-700 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-slate-50 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Process</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A structured approach to ensure successful outcomes
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', description: 'Understanding your business, objectives, and challenges' },
              { step: '02', title: 'Strategy', description: 'Developing tailored strategies and action plans' },
              { step: '03', title: 'Execution', description: 'Implementing strategies with hands-on support' },
              { step: '04', title: 'Growth', description: 'Driving results and scaling success' }
            ].map((phase, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-blue-700 mb-4">{phase.step}</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{phase.title}</h3>
                <p className="text-slate-600">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Family Office Section */}
      <section className="py-24 bg-white px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="mb-6">
                <Shield className="w-12 h-12 text-blue-700 mb-4" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Family Office Engagement</h2>
              <p className="text-lg text-slate-700 mb-6 leading-relaxed">
                We work with family offices seeking differentiated opportunities, strategic relationships and global market insights.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Our approach centers on understanding your strategic objectives and connecting you with carefully selected opportunities and partners across markets. We facilitate access through qualified partners and provide the intelligence needed for informed decision-making.
              </p>
            </div>
            
            <Card className="border-slate-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-semibold text-slate-900 mb-6">Capital Preservation & Yield Solutions</h3>
                <p className="text-slate-700 mb-6 leading-relaxed">
                  Connecting family offices with selected institutional opportunities, strategic partners and differentiated market access solutions.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-blue-700 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Access through qualified institutional partners</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-blue-700 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Capital preservation focus</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-blue-700 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Strategic partner relationships</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-slate-600">
              Common questions about our services and approach
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border border-slate-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-blue-700">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pt-2">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-blue-700 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Accelerate Your Growth?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Let's discuss how our services can support your expansion goals.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-white text-blue-700 hover:bg-slate-50 font-semibold px-8 text-base"
            >
              Schedule a Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
